/** Zahlen im deutschen Format: 2.962 · 0,45 · Jahre ohne Tausenderpunkt, v. Chr. als negative Zahl. */

// Nur „Jahr“ ist eine Jahreszahl; „Jahre“ ist eine Dauer (z. B. „Wie alt wurde …?“)
const YEAR_UNITS = new Set(["Jahr"]);

export function isYearUnit(unit: string): boolean {
  return YEAR_UNITS.has(unit);
}

export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value);
}

export function formatChips(value: number): string {
  return formatNumber(value);
}

/** Zahl ohne Einheit, Jahre ohne Tausenderpunkt und mit „v. Chr.“. */
export function formatValue(value: number, unit: string, decimals: number): string {
  if (isYearUnit(unit)) return value < 0 ? `${Math.abs(value)} v. Chr.` : String(value);
  return formatNumber(value, decimals);
}

/** Zahl mit Einheit, z. B. „2.962 Meter“ oder „44 v. Chr.“. */
export function formatWithUnit(value: number, unit: string, decimals: number): string {
  if (isYearUnit(unit)) return formatValue(value, unit, decimals);
  return `${formatNumber(value, decimals)} ${unit}`.trim();
}

/**
 * Liest eine Eingabe im deutschen Format. Punkte und Leerzeichen sind Tausendertrenner,
 * das Komma trennt Nachkommastellen. Gibt null zurück, wenn die Eingabe keine gültige Zahl ist.
 */
export function parseGermanNumber(input: string): number | null {
  const cleaned = input.trim().replace(/[\s.  ']/g, "").replace(/[−–]/g, "-").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** Hat der Wert höchstens `decimals` Nachkommastellen? */
export function fitsDecimals(value: number, decimals: number): boolean {
  const scaled = value * 10 ** decimals;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

/**
 * Formatiert die Eingabe beim Tippen mit Tausenderpunkten. Ein Komma bleibt immer stehen –
 * ist es nicht erlaubt, meldet die Prüfung das, statt Ziffern stillschweigend zu verschlucken.
 */
export function formatWhileTyping(input: string, grouping: boolean): string {
  let text = input.replace(/[^\d,.-]/g, "");
  const negative = text.startsWith("-");
  text = text.replace(/-/g, "");
  let [intPart = "", fraction] = text.replace(/\./g, "").split(",", 2);
  intPart = intPart.replace(/^0+(?=\d)/, "");
  const grouped = grouping ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : intPart;
  const hasComma = text.includes(",");
  return `${negative ? "-" : ""}${grouped}${hasComma ? `,${fraction ?? ""}` : ""}`;
}

/** Relative Zeit bis zu einer Frist in ganzen Sekunden (nie negativ). */
export function secondsLeft(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** Abweichung mit Einheit; bei Jahreszahlen „12 Jahre“ statt einer Jahreszahl. */
export function formatDeviation(deviation: number, unit: string, decimals: number): string {
  if (isYearUnit(unit)) return `${formatNumber(deviation)} ${deviation === 1 ? "Jahr" : "Jahre"}`;
  return formatWithUnit(deviation, unit, decimals);
}
